/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from 'lodash';
import { i18n } from '../../../translate/i18n';
import {
  Paper, Container, Dialog, IconButton, Link, Button, Drawer, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, Typography, Avatar, Chip, Checkbox,
} from '@mui/material';
import * as actions from "../../../store/actions";
import Spinner from '../../../components/Spinner/index';
import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import { CheckCircleOutline, HighlightOff, CloudDownload, Edit } from '@mui/icons-material'
import previewSelector from "../../../utils/previewSelector";
import ModalBackup from "../Modals/ModalBackup";

export default function TableManagementAppBackups(props) {
  const dispatch = useDispatch();
  const textInput = useRef(null);

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState({});


  const fetchBackups = useCallback(
    () => dispatch(actions.fetchBackups(page, limit, searchText, order, orderBy)),
    [page, limit, searchText, order, orderBy]
  );

  const backups = useSelector(state => state.backupsApp.backups);
  const backupsFilter = useSelector(state => state.backupsApp.backupsFilter);
  const loading = useSelector(state => state.backupsApp.loading);

  const headRows = [
    { id: "name", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementBackups.file') },
    { id: "createdAt", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementBackups.createdAt') },
    { id: "createdBy.name", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementBackups.createdBy') },
    { id: "size", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementBackups.size') },
    { id: "fileType", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementBackups.type') },
    { id: "ingested", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementBackups.ingested') },
    { id: "download", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementBackups.download') },
    { id: "edit", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementBackups.edit') },
  ];

  useEffect(() => {
    if (!isEmpty(backupsFilter)) {
      setPage(backupsFilter.page);
      setLimit(backupsFilter.limit);
      setSearchText(backupsFilter.searchText);
      setOrderBy(backupsFilter.orderBy);
      setOrder(backupsFilter.order);
    }
    setFirstLoad(false);
  }, []);

	useEffect(() => {
		dispatch(actions.setOpenDrawer(true))
	}, [])

  useEffect(() => {
    if (!firstLoad) {
      fetchBackups();
      setAlreadySerched(true);
    }
  }, [firstLoad, page, limit, order, orderBy, isModalOpen]);

  useEffect(() => {
    if (!firstLoad) {
      setPage(1);
      fetchBackups();
      setAlreadySerched(true);
    }
  }, []);

  useEffect(() => {
    if (searchText !== "" || alreadySerched) {
      const timer = setTimeout(() => {
        if (searchText === textInput.current.value) {
          fetchBackups();
        }
      }, 200);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchText, textInput]);

  const handleChangePage = (e, page) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const editBackup = (backup) => {
    setSelectedBackup(backup)
    setIsModalOpen(true);
  }

  const handleCloseModal = () => {
    setSelectedBackup({})
    setIsModalOpen(false);
  }

  return <>
    <Dialog
      scroll='body'
      fullWidth
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
    >
      <ModalBackup backup={selectedBackup} closeModal={() => handleCloseModal()} />
    </Dialog>
    <Container maxWidth="xl">
      <Paper>
        <TableTitle
          title={i18n.t('tableTitleManagementBackups.title')}
          placeholder={i18n.t('tableTitleManagementBackups.placeholder')}
          inputRef={textInput}
          value={searchText}
          onChange={event => {
            setPage(1);
            setSearchText(event.target.value);
          }}
                      showOptionsButton
        />
        <Table style={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <div style={styles.spinner}>
                    <Spinner />
                  </div>}
                {backups.rows.length === 0 &&
                  <div style={styles.noResults}>
                    {!loading && <Typography style={styles.spinner}>{i18n.t('btnFilterManagementBackups.Typography')}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            style={{ opacity: loading ? 0.3 : 1 }}
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={backups.rows ? backups.rows.length : 0}
          />
          <TableBody style={{ opacity: loading ? 0.3 : 1 }}>
            {backups.rows && backups.rows.map((backup, index) => {
              return (
                <TableRow
                  style={styles.tableRow}
                  key={backup._id}
                  hover
                >
                  <TableCell style={styles.cell}>
                      <div style={styles.name}>{backup.name}</div>
                  </TableCell>
                  <TableCell style={styles.cell}>{new Date(backup.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell style={styles.cell}>{backup.createdBy ? backup.createdBy.name : '-'}</TableCell>
                  <TableCell style={styles.cell}>{backup.size}</TableCell>
                  <TableCell style={styles.cell}>{backup.fileType}</TableCell>
                  <TableCell style={{ ...styles.cell, textAlign: 'center', color: backup.ingested ? 'green' : 'red'}}>{backup.ingested ? <CheckCircleOutline /> : <HighlightOff />}</TableCell>
                  <TableCell style={{ ...styles.cell, textAlign: 'center'}}>
                    {backup.url && (
                      <a
                        rel="noopener noreferrer"
                        href={backup.url}
                        target="_blank"
                        style={{color: 'black'}}
                      >
                        <CloudDownload />
                      </a>
                    )}
                  </TableCell>
                  <TableCell style={{ ...styles.cell, textAlign: 'center'}}>
                    <IconButton onClick={() => editBackup(backup)} size="large">
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter style={{ opacity: loading ? 0.3 : 1 }}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={backups.count}
                rowsPerPage={parseInt(limit)}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  </>;

}

const styles = {
  table: {
    position: 'relative',
    marginBottom: 50,
  },
  tableRow: {
    cursor: 'pointer'
  },
  spinner: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    top: '-5px',
    opacity: '1',
    zIndex: 2,
  },
  noResults: {
    height: '100px',
  },
  deleteTextConfirm: {
    marginLeft: '-30px',
    fontWeight: 'bold',
  },
  editWidth: {
    width: '90%'
  },
  avatar: {
    width: '2.75rem',
    height: '2.75rem',
    marginLeft: '0.25rem',
  },
  avatarSmallText: {
    fontSize: '0.75rem',
    width: '2.75rem',
    height: '2.75rem',
    marginLeft: '0.25rem',
  },
  chip: {
    margin: 5,
  },
  thumbUp: {
    marginRight: '20px',
    color: "#349c34"
  },
  thumbDown: {
    marginRight: '20px',
    color: "#d04545"
  },
  cell: {
    fontSize: '0.775rem',
    padding: '0.25rem',
  },
  cellPhotoContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginLeft: '0.5rem',
  },
  buttonFilterContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0px 20px 10px 20px',
  },
  buttonFilterIcon: {
    marginLeft: 5
  },
  buttonFilter: {
    zIndex: 200,
    backgroundColor: 'green',
    height: '2.2rem',
    width: '6.5rem',
    padding: '1rem',
    color: '#fff',
  },
  clearFilter: {
    cursor: 'pointer',
    textDecoration: 'underline',
    zIndex: 200,
    padding: '0.75rem 0.5rem',
    color: '#000',
  },
  filterContainer: {
    position: 'relative',
    width: '100%',
  },
  filterChipContainer: {
    paddingLeft: 20,
    display: 'flex',
    flexWrap: 'wrap',
  },
  filterChip: {
    cursor: 'pointer',
    margin: 5,
  },
  name: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '0.75rem',
  },
	status: {
		marginRight: '0.25rem'
	}
}



